import React, { useState, useRef, useEffect } from 'react'
import { Loader2 } from 'lucide-react'

interface EditableSelectProps {
  value: string | number | null
  onSave: (value: string | number | null) => Promise<void>
  options: { value: string | number | null; label: string }[]
  placeholder?: string
  className?: string
}

export default function EditableSelect({ 
  value, 
  onSave, 
  options, 
  placeholder = "Klicken zum Bearbeiten",
  className = ""
}: EditableSelectProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState<string>(value?.toString() || '')
  const [isSaving, setIsSaving] = useState(false)
  const selectRef = useRef<HTMLSelectElement>(null)

  useEffect(() => {
    setEditValue(value?.toString() || '')
  }, [value])

  useEffect(() => {
    if (isEditing && selectRef.current) {
      selectRef.current.focus()
    }
  }, [isEditing])

  const handleSave = async () => {
    const currentValue = value?.toString() || ''
    if (editValue === currentValue) {
      setIsEditing(false)
      return
    }

    try {
      setIsSaving(true)
      // Convert string back to original type
      const selectedOption = options.find(option => option.value?.toString() === editValue)
      const newValue = selectedOption ? selectedOption.value : null
      await onSave(newValue)
      setIsEditing(false)
    } catch (error) {
      console.error('Error saving:', error)
      // Reset to original value on error
      setEditValue(currentValue)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setEditValue(value?.toString() || '')
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  if (isEditing) {
    return (
      <div className="relative">
        <select
          ref={selectRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          className={`input-field w-full px-2 py-1 ${className}`}
          disabled={isSaving}
        >
          {options.map((option) => (
            <option key={option.value?.toString() || 'null'} value={option.value?.toString() || ''}>
              {option.label}
            </option>
          ))}
        </select>
        {isSaving && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
          </div>
        )}
      </div>
    )
  }

  const selectedOption = options.find(option => option.value?.toString() === value?.toString())
  const displayValue = selectedOption ? selectedOption.label : (value?.toString() || '')

  return (
    <div 
      className={`cursor-pointer rounded border border-transparent bg-white/70 px-2 py-1 transition-colors hover:border-gray-200 hover:bg-white dark:bg-gray-950/50 dark:hover:border-gray-600 dark:hover:bg-gray-900/80 ${className}`}
      onClick={() => setIsEditing(true)}
      title="Klicken zum Bearbeiten"
    >
      {displayValue || (
        <span className="text-gray-400 italic dark:text-gray-500">{placeholder}</span>
      )}
    </div>
  )
}
